import type { TrustSignal } from '@teentack/shared';
import { BadgeCheckIcon, ShieldCheckIcon, UsersIcon } from '@/components/ui/Icons';

/**
 * What the adults already know about this employer, said plainly.
 *
 * A teen has no connections of their own here — this is entirely the adult
 * graph, reported as counts and first names. No child is ever named, and the
 * employer never learns who is connected to whom.
 *
 * The order matters: a guardian's own judgement outranks a friend's, which
 * outranks a stranger's, so the strongest true statement is the one shown.
 */
export function TrustBadge({
  trust,
  tone = 'inline',
}: {
  trust: TrustSignal;
  tone?: 'inline' | 'block';
}) {
  const line = strongestSignal(trust);
  if (!line) return null;

  if (tone === 'block') {
    return (
      <p
        className={`flex items-start gap-2 rounded-xl border px-3.5 py-3 text-sm font-medium ${line.className}`}
      >
        {line.icon}
        <span>{line.text}</span>
      </p>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${line.chipClassName}`}
    >
      {line.icon}
      {line.short}
    </span>
  );
}

interface Line {
  text: string;
  short: string;
  icon: React.ReactNode;
  className: string;
  chipClassName: string;
}

function strongestSignal(trust: TrustSignal): Line | null {
  if (trust.guardianBlocked) {
    return {
      text: 'Your guardian has blocked this employer.',
      short: 'Blocked by guardian',
      icon: <ShieldCheckIcon className="size-4 shrink-0" />,
      className: 'border-red-200 bg-red-50 text-red-700',
      chipClassName: 'bg-red-50 text-red-700 ring-red-200',
    };
  }

  if (trust.workedTogetherCount > 0) {
    const n = trust.workedTogetherCount;
    return {
      text: `You have completed ${n} job${n === 1 ? '' : 's'} for this employer before.`,
      short: `Worked together ${n}×`,
      icon: <BadgeCheckIcon className="size-4 shrink-0" />,
      className: 'border-safe-200 bg-safe-50 text-safe-700',
      chipClassName: 'bg-safe-50 text-safe-700 ring-safe-200',
    };
  }

  if (trust.guardianTrusts) {
    return {
      text: 'Your guardian has marked this employer as trusted.',
      short: 'Trusted by your guardian',
      icon: <ShieldCheckIcon className="size-4 shrink-0" />,
      className: 'border-safe-200 bg-safe-50 text-safe-700',
      chipClassName: 'bg-safe-50 text-safe-700 ring-safe-200',
    };
  }

  if (trust.mutualParentCount > 0) {
    const names = trust.mutualParentNames;
    const rest = trust.mutualParentCount - names.length;
    const who =
      names.length === 0
        ? `${trust.mutualParentCount} parent${trust.mutualParentCount === 1 ? '' : 's'}`
        : rest > 0
          ? `${names.join(', ')} and ${rest} other${rest === 1 ? '' : 's'}`
          : names.join(', ');

    return {
      text: `${who} in your guardian's network trust this employer.`,
      short: `${trust.mutualParentCount} mutual`,
      icon: <UsersIcon className="size-4 shrink-0" />,
      className: 'border-brand-100 bg-brand-50 text-brand-800',
      chipClassName: 'bg-brand-50 text-brand-700 ring-brand-100',
    };
  }

  // Nothing true to say. Silence beats an empty reassurance.
  return null;
}
